import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Send, User, Mail, Phone, FileText, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RequestQuotePopup = ({ product, forceShow = false, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    message: ''
  });

  // Fetch popup settings from backend (with cache-busting)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const timestamp = Date.now();
        const res = await axios.get(`${API_URL}/api/marketing/popup-settings/public?_t=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        setSettings(res.data.quote_popup || {
          enabled: true,
          delay_seconds: 30,
          headline: "Need Help Deciding?",
          subheadline: "Get a personalized quote",
          cta_text: "Request Quote",
          show_phone_field: true,
          show_message_field: true
        });
      } catch (error) {
        // Use defaults if fetch fails
        setSettings({
          enabled: true,
          delay_seconds: 30,
          headline: "Need Help Deciding?",
          subheadline: "Get a personalized quote",
          cta_text: "Request Quote",
          show_phone_field: true,
          show_message_field: true
        });
      }
    };
    fetchSettings();
  }, []);

  // Handle manual show trigger
  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      setIsSubmitted(false); // Reset submitted state for manual opens
    }
  }, [forceShow]);

  useEffect(() => {
    if (!settings || !settings.enabled) return;
    
    // Check if already dismissed or submitted for this product
    const dismissedKey = `quote_popup_dismissed_${product?.id}`;
    const submittedKey = `quote_popup_submitted_${product?.id}`;
    
    if (localStorage.getItem(dismissedKey) || localStorage.getItem(submittedKey)) {
      return;
    }

    // Show popup after delay (only for auto-trigger, not manual)
    if (!forceShow) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, (settings.delay_seconds || 30) * 1000);

      return () => clearTimeout(timer);
    }
  }, [product?.id, settings, forceShow]);

  const handleClose = () => {
    setIsVisible(false);
    if (!forceShow) {
      localStorage.setItem(`quote_popup_dismissed_${product?.id}`, 'true');
    }
    if (onClose) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/leads/capture`, {
        email: formData.email,
        name: formData.name,
        phone: formData.phone || null,
        city: formData.city || null,
        province: formData.province || null,
        lead_type: 'quote',
        product_id: product?.id,
        product_name: product?.name,
        source: window.location.href,
        notes: formData.message || null
      });

      setIsSubmitted(true);
      localStorage.setItem(`quote_popup_submitted_${product?.id}`, 'true');
      toast.success('Quote request submitted! We\'ll be in touch soon.');
      
      // Auto-close after success
      setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, 3000);
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible || !product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="quote-popup-overlay">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform animate-scale-in"
        data-testid="quote-popup"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#003456] to-[#023F63] p-6 text-white relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
            data-testid="quote-popup-close"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Need Help Deciding?</h3>
              <p className="text-blue-100 text-sm">Get a personalized quote</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!isSubmitted ? (
            <>
              <p className="text-gray-600 mb-4">
                Interested in the <strong>{product.name}</strong>? Our team can help answer your questions and provide a custom quote.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      className="pl-10"
                      required
                      data-testid="quote-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      className="pl-10"
                      required
                      data-testid="quote-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone (optional)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="pl-10"
                      data-testid="quote-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="City"
                        className="pl-10"
                        data-testid="quote-city"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Province/State
                    </label>
                    <Input
                      type="text"
                      value={formData.province}
                      onChange={(e) => setFormData({...formData, province: e.target.value})}
                      placeholder="ON / CA"
                      data-testid="quote-province"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Questions or Comments
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us about your needs..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg resize-none h-24 focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                      data-testid="quote-message"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#003456] hover:bg-[#002B47] text-white py-3"
                  data-testid="quote-submit"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Request Quote
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                We typically respond within 1 business day.
              </p>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Request Received!</h4>
              <p className="text-gray-600">
                Thank you for your interest in the {product.name}. Our team will reach out to you shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestQuotePopup;
